import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { CaretUpDown, Check, Plus, Buildings, Crown } from "@phosphor-icons/react";
import { toast } from "sonner";

function OrgAvatar({ name, className = "" }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  return (
    <div className={`h-8 w-8 shrink-0 rounded-sm bg-white text-graphite-950 grid place-items-center font-display font-semibold text-sm ${className}`}>
      {initial}
    </div>
  );
}

export default function OrgSwitcher() {
  const { user, orgs, currentOrg, switchOrg, refresh } = useAuth();
  const isPlatformAdmin = !!user?.is_platform_admin;
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  // Regular users are locked to a single workspace — render a static tile with
  // no dropdown, no switcher, no create action. Their first/current org is
  // whatever `currentOrg` resolves to (even if they were grandfathered with
  // multiple memberships, we do not expose switching).
  if (!isPlatformAdmin) {
    return (
      <div
        data-testid="org-switcher-static"
        className="w-full flex items-center gap-3 rounded-md border border-white/10 bg-graphite-800 px-3 py-2.5"
      >
        <OrgAvatar name={currentOrg?.name} />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink-secondary">Workspace</div>
          <div className="truncate text-sm text-ink-primary font-medium" data-testid="org-switcher-current-name">
            {currentOrg?.name || "No workspace"}
          </div>
        </div>
      </div>
    );
  }

  const handleSelect = async (orgId) => {
    if (currentOrg?.org_id === orgId) return;
    try {
      await switchOrg(orgId);
      toast.success("Workspace switched");
    } catch {
      toast.error("Could not switch workspace");
    }
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/organizations", { name: name.trim() });
      await refresh();
      await switchOrg(data.org_id);
      toast.success("Workspace created");
      setCreating(false);
      setName("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create workspace");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="org-switcher-trigger"
            className="w-full flex items-center gap-3 rounded-md border border-white/10 bg-graphite-800 px-3 py-2.5 text-left hover:bg-graphite-700 transition-colors"
          >
            <OrgAvatar name={currentOrg?.name} />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-secondary flex items-center gap-1">
                Workspace
                <Crown weight="fill" className="h-2.5 w-2.5 text-amber-300" />
              </div>
              <div className="truncate text-sm text-ink-primary font-medium" data-testid="org-switcher-current-name">
                {currentOrg?.name || "No workspace"}
              </div>
            </div>
            <CaretUpDown weight="bold" className="h-4 w-4 text-ink-secondary shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          sideOffset={8}
          className="w-[248px] border-white/10 bg-graphite-800 text-ink-primary"
          data-testid="org-switcher-menu"
        >
          <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary font-medium">
            All workspaces
          </DropdownMenuLabel>
          {orgs.map((o) => {
            const isActive = o.org_id === currentOrg?.org_id;
            return (
              <DropdownMenuItem
                key={o.org_id}
                data-testid={`org-switcher-item-${o.slug}`}
                onClick={() => handleSelect(o.org_id)}
                className="flex items-center gap-3 px-2 py-2 cursor-pointer focus:bg-white/5"
              >
                <OrgAvatar name={o.name} />
                <div className="flex-1 min-w-0">
                  <div className="truncate text-sm">{o.name}</div>
                  <div className="text-[11px] text-ink-secondary capitalize">{o.role}</div>
                </div>
                {isActive && <Check weight="bold" className="h-4 w-4 text-ink-primary" />}
              </DropdownMenuItem>
            );
          })}
          <DropdownMenuSeparator className="bg-white/10" />
          <DropdownMenuItem
            data-testid="org-switcher-create"
            onClick={() => setCreating(true)}
            className="flex items-center gap-3 px-2 py-2 cursor-pointer focus:bg-white/5"
          >
            <div className="h-8 w-8 rounded-sm border border-dashed border-white/20 grid place-items-center">
              <Plus weight="bold" className="h-4 w-4 text-ink-secondary" />
            </div>
            <span className="text-sm">Create workspace</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent
          className="border-white/10 bg-graphite-800 text-ink-primary"
          data-testid="org-create-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display font-light tracking-tight text-2xl flex items-center gap-2">
              <Buildings weight="duotone" className="h-5 w-5" />
              New workspace
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs text-ink-secondary uppercase tracking-[0.15em]">Workspace name</label>
            <Input
              data-testid="org-create-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Design Co."
              className="bg-graphite-900 border-white/10 focus-visible:ring-white/30 focus-visible:ring-1 focus-visible:border-white/30 text-ink-primary"
            />
          </div>
          <DialogFooter>
            <button
              data-testid="org-create-cancel"
              onClick={() => setCreating(false)}
              className="rounded-md border border-white/10 px-4 py-2 text-sm hover:bg-white/5 transition-colors"
            >
              Cancel
            </button>
            <button
              data-testid="org-create-submit"
              onClick={handleCreate}
              disabled={busy || !name.trim()}
              className="rounded-md bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200 disabled:opacity-50 transition-colors"
            >
              {busy ? "Creating…" : "Create workspace"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
